import type {
  AppInventoryOptionType,
  AppInventoryState,
  AppInventoryVersionType,
} from '@openx/types/targeting/appInventory';
import {
  type AppInventoryType,
  type IntersectOptionPayload,
  type InventoryAndContentOptionPayload,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { AnyAction } from '@reduxjs/toolkit';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import type {
  SetAppInventoryTypeDispatch,
  SetComparisonTypeDispatch,
} from '../../InventoryAndContent/InventoryAndContentTopBox/constants';
import { AppInventoryComparison } from '../../InventoryAndContent/InventoryAndContentTopBox/partials/AppInventoryComparison';
import { Comparison } from '../../InventoryAndContent/InventoryAndContentTopBox/partials/Comparison';
import type { DrawerComponentsMap } from '../../InventoryAndContent/InventoryAndContentTopBox/utils/filterTypes';
import { getItemsCount } from '../../InventoryAndContent/InventoryAndContentTopBox/utils/getItemsCount';
import type { InvalidItemsError } from '../../shared/BulkAdd/types';

export type UseGetDrawerContentProps = {
  targetingParams: AppInventoryState;
  contentOption: AppInventoryOptionType;
  comparisonType: ComparisonType;
  dispatchInventory: (action: AnyAction) => void;
  setComparisonType: SetComparisonTypeDispatch;
  // App Inventory specific props
  appInventoryType?: AppInventoryType;
  setAppInventoryType?: SetAppInventoryTypeDispatch;
  appNameComparisonType?: ComparisonType;
  bundleIdComparisonType?: ComparisonType;
  accountUid?: string;
  setAppBundleIdDuplicateErrors?: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryVersion?: AppInventoryVersionType;
};

export const useGetDrawerContent = ({
  dispatchInventory,
  targetingParams,
  contentOption,
  comparisonType,
  setComparisonType,
  appInventoryType: appInventoryTypeProp,
  setAppInventoryType: setAppInventoryTypeProp,
  appNameComparisonType: appNameComparisonTypeProp,
  bundleIdComparisonType: bundleIdComparisonTypeProp,
  accountUid,
  setAppBundleIdDuplicateErrors,
  inventoryVersion,
}: UseGetDrawerContentProps) => {
  const inventoryContent = targetingParams[contentOption] as InventoryAndContentOptionPayload | IntersectOptionPayload;

  const drawerContent: Partial<DrawerComponentsMap> = useMemo(() => {
    return {
      [InventoryContentOption.APP_BUNDLE_ID]: (
        <Comparison
          contentOption={contentOption}
          comparisonType={comparisonType}
          dispatchInventory={dispatchInventory}
          inventoryContent={inventoryContent}
          itemsCount={getItemsCount(inventoryContent?.val)}
          setComparisonType={setComparisonType}
        />
      ),
      // App Inventory uses AppInventoryComparison with type dropdown + input
      // Toggle ALLOW/BLOCK is in the Items section below
      [InventoryContentOption.APP_GROUP]: (
        <AppInventoryComparison
          appInventoryType={appInventoryTypeProp}
          setAppInventoryType={setAppInventoryTypeProp}
          appNameComparisonType={appNameComparisonTypeProp}
          bundleIdComparisonType={bundleIdComparisonTypeProp}
          dispatchInventory={dispatchInventory}
          appInventoryContent={targetingParams[InventoryContentOption.APP_GROUP]}
          appBundleIdContent={targetingParams[InventoryContentOption.APP_BUNDLE_ID]}
          accountUid={accountUid}
          setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
          inventoryVersion={inventoryVersion}
        />
      ),
    };
  }, [
    inventoryContent,
    dispatchInventory,
    targetingParams,
    contentOption,
    comparisonType,
    setComparisonType,
    appInventoryTypeProp,
    setAppInventoryTypeProp,
    appNameComparisonTypeProp,
    bundleIdComparisonTypeProp,
    accountUid,
    setAppBundleIdDuplicateErrors,
    inventoryVersion,
  ]);

  return { drawerContent };
};
